import { useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import AdminTable from 'layouts/AdminTable';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import withStyles from '@material-ui/core/styles/withStyles';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import gastro from 'helpers/gastro';
import { post, put } from 'helpers/apiHelpers';
import { combineStyles } from 'helpers/helpers';
import FormTextInputTranslatable from 'components/FormTextInput/FormTextInputTranslatable';
import { isTranslatableValuesObjectEmpty } from 'hooks/redux/Translatable/useTranslatableLanguages';
import { toast } from 'react-toastify';

import defaultState from './defaultState';

const Form = ({ match, classes, history, valueUrl, valueKey, location }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({ ...defaultState });

  const isEdit = location.pathname.includes('edit');
  const elementId = match.params.id;

  const displayModes = [
    {
      value: 0,
      label: t('form.field.displayMode.onlyName', 'Sama nazwa'),
    },
    { value: 1, label: t('form.field.displayMode.onlyIcon', 'Sama ikona') },
    {
      value: 2,
      label: t('form.field.displayMode.nameAndIcon', '$$Nazwa i ikona'),
    },
  ];

  useEffect(() => {
    if (isEdit) {
      // eslint-disable-next-line no-use-before-define -- FIXME
      fetchValue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const fetchValue = () => {
    gastro
      .get(`${valueUrl}/${elementId}?translations=true`)
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(({ data }) => {
        setState({
          ...state,
          ...data,
          '@resources': {
            // eslint-disable-next-line no-negated-condition -- FIXME
            ...(!isEmpty(data?.icon)
              ? { [data?.icon?.['@id']]: data?.icon }
              : {}),
          },
          name: data?.value,
          icon: data?.icon?.['@id'],
          position: data?.position,
          iconVisible: data?.iconVisible,
        });
      });
  };

  const handleInputChange = event => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  const validateForm = () => {
    const isNameEmpty = isTranslatableValuesObjectEmpty(state.name);
    if (isNameEmpty) {
      toast.error(t('form.nameFieldCannotBeEmpty'));
    }

    if (state.iconVisible === 1 && !state.icon) {
      toast.error(t('form.iconCannotBeEmptyIfDisplayIconModeIsOn'));
    }

    return !isNameEmpty && !(state.iconVisible === 1 && !state.icon);
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const data = {
      [valueKey || 'value']: state.name,
      // eslint-disable-next-line radix -- FIXME
      position: parseInt(state.position),
      icon: state?.icon,
      iconVisible: state?.iconVisible,
    };

    const action = isEdit
      ? put(`${valueUrl}/${elementId}`, data)
      : post(valueUrl, data);

    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    action.then(() => history.push(`/admin/${valueUrl}`));
  };

  const geteClientTagIcon = (fieldName, data) => {
    setState({
      ...state,
      '@resources': {
        ...(state?.['@resources'] ?? {}),
        [data?.['@id']]: { ...data },
      },
      [fieldName]: data?.['@id'] ?? null,
    });
  };

  const removeClientTagIcon = fieldName => {
    setState({
      ...state,
      [fieldName]: null,
    });
  };

  return (
    <AdminTable title={isEdit ? t('form.editValue') : t('form.addNewValue')}>
      <GridContainer>
        <GridItem md={4}>
          <FormTextInputTranslatable
            label={`${t('form.field.name')}*`}
            name="name"
            value={state.name}
            handleChange={handleInputChange}
          />
        </GridItem>
        <GridItem md={2}></GridItem>
        <GridItem md={4}>
          <FormTextInput
            label={t('form.field.order')}
            classes={classes}
            name="position"
            value={state.position}
            type="number"
            handleChange={handleInputChange}
          />
        </GridItem>
        <GridItem md={4}>
          <FormLabel className={classes.labelHorizontal}>
            {t('form.field.displayMode', '$$Sposób wyświetlenia')}
          </FormLabel>
          <FormControl fullWidth={true} className={classes.selectFormControl}>
            <Select
              value={state?.iconVisible}
              onChange={event =>
                setState({ ...state, iconVisible: event.target.value })
              }
              name="iconVisible"
              classes={{ select: classes.select }}
              MenuProps={{ className: classes.selectMenu }}
            >
              {displayModes.map(({ value, label }, index) => (
                <MenuItem
                  key={index}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelectedMultiple,
                  }}
                  value={value}
                >
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem md={2}></GridItem>
        <GridItem md={4}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FormLabel className={classes.labelHorizontal}>
              {t('form.field.icon', '$$Ikona')}
            </FormLabel>
          </div>
          <FormImageUpload
            stateName="icon"
            buttonText={t('common.shared.select', 'Wybierz')}
            getImage={geteClientTagIcon}
            imgId="icon"
            removeImage={removeClientTagIcon}
            previewUrl={state?.['@resources']?.[state?.icon]?.contentUrl}
          />
        </GridItem>
        <FormControlButtons
          classes={classes}
          discardText={t('form.cancel')}
          submitText={t('form.save')}
          cancelPath={`/admin/${valueUrl}`}
          handleSubmit={handleSubmit}
        />
      </GridContainer>
    </AdminTable>
  );
};

const combinedStyles = combineStyles(extendedFormsStyle, buttonsStyle);

export default withStyles(combinedStyles)(Form);
